import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { Crown, ChevronUp, Trophy, Users, TrendingUp } from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";
import { useTranslation } from "react-i18next";
import { makeTr } from "@/i18n/tr";

// League colors and gradients
const LEAGUE_STYLES = {
  Bronze: {
    gradient: "from-amber-700 to-amber-900",
    border: "border-amber-600",
    text: "text-amber-500",
    bg: "bg-amber-500/10"
  },
  Silver: {
    gradient: "from-gray-300 to-gray-500",
    border: "border-gray-400",
    text: "text-gray-400",
    bg: "bg-gray-400/10"
  },
  Gold: {
    gradient: "from-yellow-400 to-yellow-600",
    border: "border-yellow-500",
    text: "text-yellow-500",
    bg: "bg-yellow-500/10"
  },
  Platinum: {
    gradient: "from-cyan-200 to-cyan-400",
    border: "border-cyan-300",
    text: "text-cyan-300",
    bg: "bg-cyan-300/10"
  },
  Diamond: {
    gradient: "from-blue-300 to-blue-500",
    border: "border-blue-400",
    text: "text-blue-400",
    bg: "bg-blue-400/10"
  },
  Master: {
    gradient: "from-purple-400 to-purple-600",
    border: "border-purple-500",
    text: "text-purple-500",
    bg: "bg-purple-500/10"
  },
  Grandmaster: {
    gradient: "from-red-400 to-red-600",
    border: "border-red-500",
    text: "text-red-500",
    bg: "bg-red-500/10"
  },
  Legend: {
    gradient: "from-orange-400 to-orange-600",
    border: "border-orange-500",
    text: "text-orange-500",
    bg: "bg-orange-500/10"
  }
};

export function LeagueBadge({ league, size = "md", showProgress = false }) {
  const { i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  if (!league) return null;
  
  const style = LEAGUE_STYLES[league.name] || LEAGUE_STYLES.Bronze;
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-3 py-1",
    lg: "text-base px-4 py-2"
  };
  
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <Badge 
        className={`${style.bg} ${style.text} ${style.border} border ${sizeClasses[size]}`}
      >
        <span className="mr-1">{league.icon}</span>
        {league.name}
      </Badge>
      {showProgress && league.next_league && (
        <div className="w-full">
          <Progress value={league.progress} className="h-1" />
          <p className="text-[10px] text-muted-foreground text-center mt-0.5">
            {league.points_to_next} {tr("до", "to")} {league.next_league}
          </p>
        </div>
      )}
    </div>
  );
}

export default function LeagueCard({ token }) {
  const { i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const [leagueData, setLeagueData] = useState(null);
  const [allLeagues, setAllLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeagueData();
  }, [token]);

  const loadLeagueData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [myLeagueRes, allLeaguesRes] = await Promise.all([
        axios.get(`${API_URL}/leagues/my`, { headers }),
        axios.get(`${API_URL}/leagues`)
      ]);
      setLeagueData(myLeagueRes.data);
      setAllLeagues(allLeaguesRes.data.leagues);
    } catch (err) {
      console.error("Failed to load league data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass">
        <CardContent className="py-8 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!leagueData) return null;

  const { league, rating, rank_in_league, players_in_league } = leagueData;
  const style = LEAGUE_STYLES[league.name] || LEAGUE_STYLES.Bronze;

  return (
    <Card className={`glass ${style.border} border-2`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Crown className={`h-5 w-5 ${style.text}`} />
          {tr("Ваша Лига", "Your League")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Current League Display */}
        <div className={`rounded-lg p-4 mb-4 bg-gradient-to-r ${style.gradient} bg-opacity-20`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{league.icon}</span>
              <div>
                <h3 className="text-2xl font-black">{league.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Tier {league.tier} / {league.total_tiers}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{rating}</p>
              <p className="text-xs text-muted-foreground">{tr("рейтинг", "rating")}</p>
            </div>
          </div>
        </div>

        {/* Progress to Next League */}
        {league.next_league && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">{tr("Прогресс до", "Progress to")} {league.next_league}</span>
              <span className={style.text}>{league.progress}%</span>
            </div>
            <Progress value={league.progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1 text-center">
              <TrendingUp className="h-3 w-3 inline mr-1" />
              {tr("Осталось", "Remaining")} {league.points_to_next} {tr("очков", "points")}
            </p>
          </div>
        )}

        {/* League Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <Trophy className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-bold">#{rank_in_league}</p>
            <p className="text-xs text-muted-foreground">{tr("Место в лиге", "League rank")}</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <Users className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-bold">{players_in_league}</p>
            <p className="text-xs text-muted-foreground">{tr("Игроков в лиге", "Players in league")}</p>
          </div>
        </div>

        {/* All Leagues Preview */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-xs text-muted-foreground mb-2">{tr("Все лиги:", "All leagues:")}</p>
          <div className="flex flex-wrap gap-1">
            {allLeagues.map((l, i) => (
              <Badge
                key={l.name}
                variant="outline"
                className={`text-[10px] ${l.name === league.name ? style.bg + ' ' + style.text : 'opacity-50'}`}
              >
                {l.icon} {l.min_rating}+
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
